import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import {
  FlujoVideoDashcam,
  FuncionCanalDashcam,
  ICanalDashcam,
  ICreateDashcam,
  IDashcam,
  IPopulate,
  IQueryParam,
  IServicioContratado,
  ISim,
  IUpdateDashcam,
} from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { ICONO_DASHCAM } from '../../../../../auxiliares/iconos/iconos';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DashcamsService } from '../../../../../auxiliares/servicios/http/dashcams.service';
import { SimsService } from '../../../../../auxiliares/servicios/http/sims.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { BOTONES } from '../../../../../componentes/botones';
import { PasosNavComponent } from '../../../../../componentes/formularios/pasos-nav/pasos-nav.component';
import { ConfigHorariosAtencionComponent } from '../../../../standalone/config-horarios-atencion/config-horarios-atencion.component';
import { JerarquiaClienteComponent } from '../../../../standalone/jerarquia-cliente/jerarquia-cliente.component';
import { LoginService } from '../../../../login/login.service';
import { SimConfigComponent } from '../../../alarmas/alarmas-domiciliarias/crear-alarma-domiciliaria-completo/sim-config/sim-config.component';

export interface ICrearEditarDashcamData {
  edit?: IDashcam;
  /**
   * Alta desde el formulario de un activo: la dashcam nace en el cliente del
   * activo y el selector de cliente no se muestra.
   */
  idCliente?: string;
}

/** El "teléfono" JT/T 808 de la cámara: sólo dígitos y sin ceros adelante. */
const UNIQUE_ID = /^[1-9][0-9]*$/;

/** La G40 Pro trae dos cámaras internas: frontal (con audio) y cabina. */
const CANALES_POR_DEFECTO: ICanalDashcam[] = [
  {
    numero: 1,
    nombre: 'Frontal',
    funcion: 'Frontal',
    habilitado: true,
    audio: true,
  },
  {
    numero: 2,
    nombre: 'Cabina',
    funcion: 'Cabina',
    habilitado: true,
    audio: false,
  },
];

/**
 * Alta y edición de una dashcam, por pasos. La asignación a un activo NO va
 * acá: se elige la dashcam desde el formulario del vehículo, activo o
 * colectivo (igual que el tracker). Lo que informa la cámara de sí misma
 * (`equipo`) tampoco: lo completa el servidor y se ve en el detalle.
 */
@Component({
  selector: 'app-crear-editar-dashcam',
  standalone: true,
  imports: [
    AuxiliaresModule,
    BOTONES,
    PasosNavComponent,
    SimConfigComponent,
    ConfigHorariosAtencionComponent,
    JerarquiaClienteComponent,
  ],
  templateUrl: './crear-editar-dashcam.component.html',
  styleUrl: './crear-editar-dashcam.component.scss',
})
export class CrearEditarDashcamComponent implements OnInit {
  helper = inject(HelperService);
  private service = inject(DashcamsService);
  private simsService = inject(SimsService);
  private lista2 = inject(Lista2Service);
  private dialogService = inject(DialogService);
  data = inject<ICrearEditarDashcamData>(MAT_DIALOG_DATA, { optional: true });
  dialogRef = inject<MatDialogRef<CrearEditarDashcamComponent>>(MatDialogRef);

  @ViewChild('stepper') stepper?: MatStepper;

  public readonly icono = ICONO_DASHCAM;
  public loading = true;
  public guardando = false;
  public id?: string;
  public dashcam?: IDashcam;
  public title = 'Crear dashcam';
  public subtitle?: string;
  public form?: FormGroup;
  public servicios: IServicioContratado[] = [];
  public clientePropio = LoginService.getCliente();
  public tipoCliente = HelperService.getTipoCliente();

  public readonly funciones: FuncionCanalDashcam[] = [
    'Frontal',
    'Cabina',
    'Trasera',
    'Lateral',
    'Carga',
    'Otra',
  ];
  public readonly flujos: { valor: FlujoVideoDashcam; etiqueta: string }[] = [
    { valor: 'Secundario', etiqueta: 'Secundario (consume menos datos)' },
    { valor: 'Principal', etiqueta: 'Principal (mejor calidad)' },
  ];

  get datos(): FormGroup {
    return this.form?.get('datos') as FormGroup;
  }

  get comunicacion(): FormGroup {
    return this.form?.get('comunicacion') as FormGroup;
  }

  get sim1(): FormGroup {
    return this.comunicacion?.get('sim1') as FormGroup;
  }

  get sim2(): FormGroup {
    return this.comunicacion?.get('sim2') as FormGroup;
  }

  get video(): FormGroup {
    return this.form?.get('video') as FormGroup;
  }

  get canales(): FormArray<FormGroup> {
    return this.video?.get('canales') as FormArray<FormGroup>;
  }

  get clienteForm(): FormGroup {
    return this.form?.get('cliente') as FormGroup;
  }

  get idCliente(): string | null {
    return this.clienteForm?.get('idCliente')?.value ?? null;
  }

  /** El cliente viene fijo cuando el alta se abre desde un activo. */
  get clienteFijo(): boolean {
    return !!this.data?.idCliente;
  }

  /// Formulario

  private grupoSim(sim?: ISim): FormGroup {
    return new FormGroup({
      // SIM vinculada o elegida en el selector de disponibles
      _id: new FormControl(sim?._id),
      iccid: new FormControl(sim?.iccid),
      numero: new FormControl(sim?.numero),
      operador: new FormControl(sim?.operador),
      apn: new FormControl(sim?.apn),
      usuario: new FormControl(sim?.usuario),
      password: new FormControl(sim?.password),
    });
  }

  private grupoCanal(canal?: ICanalDashcam): FormGroup {
    return new FormGroup({
      numero: new FormControl<number | null>(canal?.numero ?? null, [
        Validators.required,
        Validators.min(1),
      ]),
      nombre: new FormControl(canal?.nombre ?? null),
      funcion: new FormControl<FuncionCanalDashcam | null>(
        canal?.funcion ?? null,
      ),
      habilitado: new FormControl(canal?.habilitado ?? true),
      audio: new FormControl(canal?.audio ?? false),
    });
  }

  private createForm(): void {
    const d = this.dashcam;
    const canales = d?.canales?.length ? d.canales : CANALES_POR_DEFECTO;
    const duracion = d?.configVideo?.duracionMaximaEnVivo;

    this.form = new FormGroup({
      datos: new FormGroup({
        nombre: new FormControl(d?.nombre, Validators.required),
        identificacion: new FormControl(d?.identificacion),
        idModelo: new FormControl(d?.idModelo, Validators.required),
        uniqueId: new FormControl(d?.uniqueId, [
          Validators.required,
          Validators.pattern(UNIQUE_ID),
        ]),
        numeroSerie: new FormControl(d?.numeroSerie),
      }),
      comunicacion: new FormGroup({
        sim1: this.grupoSim(d?.sim1),
        sim2: this.grupoSim(d?.sim2),
        // En minutos, como el del tracker (lo usa el estado de conexión)
        frecReporte: new FormControl(d?.frecReporte, [Validators.min(1)]),
      }),
      video: new FormGroup({
        canales: new FormArray(canales.map((c) => this.grupoCanal(c))),
        flujoEnVivo: new FormControl<FlujoVideoDashcam>(
          d?.configVideo?.flujoEnVivo ?? 'Secundario',
        ),
        // El modelo lo guarda en segundos; en pantalla va en minutos.
        duracionMaximaMinutos: new FormControl<number | null>(
          duracion ? Math.round(duracion / 60) : 5,
          [Validators.min(1)],
        ),
        solicitarEvidencia: new FormControl(
          d?.configVideo?.solicitarEvidencia ?? false,
        ),
      }),
      cliente: new FormGroup({
        idCliente: new FormControl(
          this.data?.idCliente ?? d?.idCliente ?? this.clientePropio?._id,
          Validators.required,
        ),
        configHorariosAtencion: new FormControl(
          d?.configHorariosAtencion ??
            (this.clientePropio?._id
              ? [{ idCliente: this.clientePropio._id }]
              : null),
        ),
        idServiciosContratados: new FormControl(d?.idServiciosContratados),
      }),
    });
  }

  /// Canales

  public agregarCanal(): void {
    const usados = this.canales.controls.map((c) => +c.value.numero || 0);
    const numero = usados.length ? Math.max(...usados) + 1 : 1;
    this.canales.push(this.grupoCanal({ numero, habilitado: true }));
    this.canales.markAsDirty();
  }

  public quitarCanal(i: number): void {
    this.canales.removeAt(i);
    this.canales.markAsDirty();
  }

  /** Dos canales con el mismo número: la cámara no los distingue. */
  get canalesRepetidos(): boolean {
    const numeros = this.canales.controls
      .map((c) => c.value.numero)
      .filter((n) => n !== null && n !== undefined && n !== '');
    return new Set(numeros.map(Number)).size !== numeros.length;
  }

  /// SIMs

  /** Un slot de SIM se persiste si tiene ALGÚN dato, no sólo ICCID. */
  private simTieneDatos(sim: any): boolean {
    if (!sim) return false;
    return ['iccid', 'numero', 'operador', 'apn', 'usuario', 'password'].some(
      (k) => !!sim[k],
    );
  }

  /**
   * SIM como entidad aparte, igual que en el tracker: cada slot con datos se
   * persiste (POST /sims/upsert) y queda vinculado por idSim1/idSim2.
   */
  private async aplicarSims(data: ICreateDashcam | IUpdateDashcam) {
    for (const slot of [1, 2] as const) {
      const simVal = (slot === 1 ? this.sim1 : this.sim2).value;
      const idField = slot === 1 ? 'idSim1' : 'idSim2';
      if (this.simTieneDatos(simVal)) {
        const { _id, ...datos } = simVal;
        const sim = await this.simsService.upsert(
          _id ? { ...datos, _id } : datos,
        );
        data[idField] = sim._id;
      } else {
        data[idField] = null;
      }
    }
  }

  /**
   * Las SIMs son FK con virtual: sólo llegan si quien abre el formulario pidió
   * populate. Si hay FK pero no vienen, se traen acá.
   */
  private async hidratarSims(): Promise<void> {
    const d = this.dashcam;
    if (!d?._id) return;
    const faltan = (!!d.idSim1 && !d.sim1) || (!!d.idSim2 && !d.sim2);
    if (!faltan) return;
    const populate: IPopulate[] = [{ path: 'sim1' }, { path: 'sim2' }];
    const query: IQueryParam = {
      filter: JSON.stringify({ _id: d._id }),
      populate: JSON.stringify(populate),
      limit: 1,
    };
    try {
      const conSims = (await this.service.getFiltered(query))?.datos?.[0];
      if (conSims) {
        this.dashcam = { ...d, sim1: conSims.sim1, sim2: conSims.sim2 };
      }
    } catch (error) {
      console.error(error);
    }
  }

  /// Guardar

  private getData(): ICreateDashcam {
    const { datos, comunicacion, video, cliente } = this.form!.value;
    const limpio = (x?: string | null) => x?.trim() || undefined;
    const canales: ICanalDashcam[] = (video.canales ?? []).map(
      (c: ICanalDashcam) => ({
        numero: Number(c.numero),
        nombre: limpio(c.nombre),
        funcion: c.funcion ?? undefined,
        habilitado: !!c.habilitado,
        audio: !!c.audio,
      }),
    );
    const idsAtencion = (cliente.configHorariosAtencion ?? [])
      .map((c: any) => c.idCliente)
      .filter(Boolean) as string[];

    return {
      nombre: limpio(datos.nombre),
      identificacion: limpio(datos.identificacion),
      idModelo: datos.idModelo,
      uniqueId: limpio(datos.uniqueId),
      numeroSerie: limpio(datos.numeroSerie),
      frecReporte: comunicacion.frecReporte || undefined,
      canales,
      configVideo: {
        flujoEnVivo: video.flujoEnVivo ?? undefined,
        duracionMaximaEnVivo: video.duracionMaximaMinutos
          ? Number(video.duracionMaximaMinutos) * 60
          : undefined,
        solicitarEvidencia: !!video.solicitarEvidencia,
      },
      idCliente: this.data?.idCliente ?? cliente.idCliente,
      configHorariosAtencion: cliente.configHorariosAtencion ?? undefined,
      idsClientesQuePuedenAtenderEventos: idsAtencion,
      idServiciosContratados: cliente.idServiciosContratados ?? undefined,
    };
  }

  public async guardar(): Promise<void> {
    if (!this.form || this.guardando) return;
    if (this.form.invalid || this.canalesRepetidos) {
      this.form.markAllAsTouched();
      this.helper.notifError(
        this.canalesRepetidos
          ? 'Hay dos canales con el mismo número.'
          : 'Revisá los campos marcados en rojo.',
        'Cerrar',
        true,
      );
      return;
    }

    const verbo = this.id ? 'guardar los cambios de' : 'crear';
    let ok = await this.dialogService.confirm(
      this.title,
      `¿Querés ${verbo} la dashcam?`,
    );
    if (!ok) return;

    const uniqueId = this.datos.value.uniqueId?.trim();
    if (this.id && uniqueId !== this.dashcam?.uniqueId) {
      ok = await this.dialogService.confirm(
        this.title,
        'Cambiaste el ID único: la cámara que tenga el anterior deja de ' +
          'reconocerse como esta dashcam. ¿Querés continuar?',
      );
      if (!ok) return;
    }

    this.guardando = true;
    try {
      const data = this.getData();
      await this.aplicarSims(data);
      const result = this.id
        ? await this.service.update(this.id, data as IUpdateDashcam)
        : await this.service.create(data);
      this.helper.notifSuccess(
        this.id ? 'Dashcam actualizada' : 'Dashcam creada',
      );
      this.dialogRef.close(result);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.guardando = false;
    }
  }

  public cerrar(): void {
    this.dialogRef.close();
  }

  private async listarServicios(): Promise<void> {
    const query: IQueryParam = { sort: 'nombre', select: 'nombre icono' };
    try {
      this.servicios = (
        await firstValueFrom(this.lista2.serviciosContratados(query))
      ).datos;
    } catch (error) {
      console.error(error);
      this.servicios = [];
    }
  }

  async ngOnInit(): Promise<void> {
    this.dashcam = this.data?.edit;
    this.id = this.dashcam?._id;
    if (this.id) {
      this.title = 'Editar dashcam';
      this.subtitle = DashcamsService.nombre(this.dashcam);
      await this.hidratarSims();
    }
    this.createForm();
    await this.listarServicios();
    this.loading = false;
  }
}
